"use client";

import { Rows3, LayoutList } from "lucide-react";
import { ViewSwitcher } from "../view-switcher";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { cn } from "../../../lib/utils";
import type { DataTableViewMode, GroupableField } from "./types";

export interface DataTableGroupingControlsProps<TData> {
  groupableBy: GroupableField<TData>[];
  viewMode: DataTableViewMode;
  onViewModeChange: (value: DataTableViewMode) => void;
  groupBy?: Extract<keyof TData, string>;
  onGroupByChange: (value: Extract<keyof TData, string>) => void;
  className?: string;
}

export function DataTableGroupingControls<TData>({
  groupableBy,
  viewMode,
  onViewModeChange,
  groupBy,
  onGroupByChange,
  className,
}: DataTableGroupingControlsProps<TData>) {
  if (!groupableBy.length) return null;

  const activeGroupBy = groupBy ?? groupableBy[0].id;
  const isGrouped = viewMode === "grouped";

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <ViewSwitcher
        options={[
          {
            value: "flat",
            label: "Flat view",
            icon: <Rows3 className="h-4 w-4" />,
          },
          {
            value: "grouped",
            label: "Grouped view",
            icon: <LayoutList className="h-4 w-4" />,
          },
        ]}
        value={viewMode}
        onValueChange={(v) => onViewModeChange(v as DataTableViewMode)}
      />
      {isGrouped && groupableBy.length > 1 && (
        /*
          Job A — CHOOSE (`docs/picker-taxonomy.md` §2.1). `variant="compact"`
          for a toolbar; the width moves to this parent. `h-9 w-[180px]` was
          both a padding-derived height and an arbitrary width (§9.3-16), and
          the group-by control rendered at a different height from every other
          control on the same bar.

          `compact` carries no width of its own (`inline-flex … max-w-full
          shrink-0`), so unlike `field` / `inline` it does not stretch to its
          parent — the trigger has to be told to fill, or the wrapper's width is
          inert and the control collapses to its label.
        */
        <div className="w-44 shrink-0">
          <Select
            value={activeGroupBy}
            onValueChange={(v) =>
              onGroupByChange(v as Extract<keyof TData, string>)
            }
          >
            <SelectTrigger
              variant="compact"
              className="w-full"
              aria-label="Group by"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {groupableBy.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  Group by: {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

DataTableGroupingControls.displayName = "DataTableGroupingControls";
